<template>
  <div class="unidades-form-overlay">
    <div
      class="unidades-form-card"
      @click.stop
    >
      <!-- Cerrar -->
      <button
        type="button"
        class="unidades-form-close"
        @click="$emit('cerrar')"
      >
        ✕
      </button>

      <!-- Header -->
      <header class="unidades-form-header">
        <div class="unidades-form-header-main">
          <p class="unidades-form-title">
            Registrar unidades
          </p>
          <p class="unidades-form-subtitle">
            Crea varias unidades del producto principal y asigna sus complementos en un solo paso.
          </p>
        </div>
      </header>

      <!-- Formulario -->
      <form class="unidades-form-body" @submit.prevent="guardar">
        <!-- Producto + cantidad -->
        <section class="unidades-form-row">
          <div class="unidades-form-field unidades-form-field--grow">
            <label class="unidades-form-label">
              Producto principal
            </label>
            <SelectorProducto
              :productos="productosPrincipales"
              v-model="form.id_producto"
              @seleccionado="alSeleccionarProducto"
            />
          </div>

          <div class="unidades-form-field unidades-form-field--compact">
            <label class="unidades-form-label">
              Cantidad de unidades
            </label>

            <div class="unidades-counter">
              <button
                type="button"
                class="unidades-counter__btn"
                @click="decrementCantidad"
              >
                −
              </button>

              <input
                type="number"
                min="1"
                v-model.number="cantidad"
                @input="onCantidadInput"
                class="unidades-input unidades-counter__input"
                @focus="abrirCantidadModalDesdeInput"
              />

              <button
                type="button"
                class="unidades-counter__btn"
                @click="incrementCantidad"
              >
                +
              </button>
            </div>
          </div>
        </section>

        <!-- Info si no hay producto aún -->
        <p
          v-if="!form.id_producto"
          class="unidades-form-hint"
        >
          Selecciona un producto principal para comenzar a registrar sus unidades y complementos.
        </p>

        <section class="unidades-form-mode">
          <div class="unidades-form-mode__head">
            <div>
              <label class="unidades-form-label unidades-form-label--strong">
                Evidencia inicial de la carga
              </label>
              <p class="unidades-form-hint">
                Puedes aplicar la misma descripción y foto a toda la carga o registrar evidencia distinta por cada unidad principal.
              </p>
            </div>
          </div>

          <div class="unidades-mode-toggle" role="group" aria-label="Modo de evidencia inicial">
            <button
              type="button"
              class="unidades-mode-toggle__btn"
              :class="{ 'unidades-mode-toggle__btn--active': evidenciaModo === 'global' }"
              @click="evidenciaModo = 'global'"
            >
              Una sola evidencia para toda la carga
            </button>
            <button
              type="button"
              class="unidades-mode-toggle__btn"
              :class="{ 'unidades-mode-toggle__btn--active': evidenciaModo === 'individual' }"
              @click="evidenciaModo = 'individual'"
            >
              Una evidencia por unidad
            </button>
          </div>
        </section>

        <section v-if="evidenciaModo === 'global'" class="unidades-form-state">
          <div class="unidades-form-field unidades-form-field--grow">
            <label class="unidades-form-label">
              Descripción del estado inicial
            </label>
            <textarea
              v-model="descripcionEstado"
              class="unidades-input unidades-input--light unidades-input--textarea"
              rows="3"
              placeholder="Ej. Equipo nuevo, caja cerrada, sin golpes visibles."
            ></textarea>
          </div>

          <div class="unidades-form-field">
            <label class="unidades-form-label">
              Foto del estado inicial
            </label>

            <div class="unidades-photo-box">
              <div v-if="imagenEstadoPreview" class="unidades-photo-preview">
                <img :src="imagenEstadoPreview" alt="Estado inicial de la carga" />
              </div>
              <div v-else class="unidades-photo-empty">
                Sin foto global
              </div>

              <input
                ref="inputImagenEstado"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                class="unidades-photo-input"
                @change="onImagenEstadoSeleccionada"
              />

              <div class="unidades-photo-actions">
                <button
                  type="button"
                  class="ios-ghost-button"
                  @click="$refs.inputImagenEstado && $refs.inputImagenEstado.click()"
                >
                  {{ imagenEstadoPreview ? 'Cambiar foto global' : 'Cargar foto global' }}
                </button>

                <button
                  v-if="imagenEstadoPreview"
                  type="button"
                  class="ios-ghost-button"
                  @click="quitarImagenEstado"
                >
                  Quitar
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="unidades-form-state unidades-form-state--single">
          <div class="unidades-form-field unidades-form-field--grow">
            <label class="unidades-form-label unidades-form-label--strong">
              Atajo de ubicación para esta carga
            </label>
            <p class="unidades-form-hint">
              Esta ubicación no se toma como base automática. Úsala como atajo y aplícala sólo si quieres copiarla a todas las unidades de esta carga.
            </p>
            <SelectorUbicacionAlmacen
              :model-value="ubicacionGlobal"
              :almacenes="almacenesCatalogo"
              @update:modelValue="onUbicacionGlobalChange"
              @created="onUbicacionCreadaGlobal"
            />
            <div class="unidades-form-shortcut-actions">
              <button
                type="button"
                class="ios-ghost-button"
                :disabled="!ubicacionGlobal.id_almacen || !ubicacionGlobal.id_ubicacion_almacen"
                @click="aplicarUbicacionGlobalATodas"
              >
                Aplicar esta ubicación a todas las unidades
              </button>
            </div>
          </div>

          <div
            v-if="form.id_producto"
            class="unidades-bulk-shortcuts"
          >
            <div class="unidades-bulk-shortcuts__card">
              <label class="unidades-form-label unidades-form-label--strong">
                Categoría rápida para toda la carga
              </label>
              <p class="unidades-form-hint">
                Elige si las unidades de esta carga entran como nuevas o usadas y aplícalo a todas de una vez.
              </p>
              <select
                v-model="categoriaGlobal"
                class="unidades-input unidades-input--light unidades-input--compact"
                @change="categoriaGlobalAplicada = false"
              >
                <option value="nuevo">Nuevo</option>
                <option value="usado">Usado</option>
              </select>
              <div class="unidades-form-shortcut-actions unidades-form-shortcut-actions--start">
                <button
                  type="button"
                  class="ios-ghost-button"
                  @click="aplicarCategoriaGlobalATodas"
                >
                  Aplicar esta categoría a todas las unidades
                </button>
              </div>
            </div>

            <div
              v-if="modelosProductoSeleccionado.length"
              class="unidades-bulk-shortcuts__card"
            >
              <label class="unidades-form-label unidades-form-label--strong">
                Modelo rápido para toda la carga
              </label>
              <p class="unidades-form-hint">
                Si este lote comparte el mismo modelo, selecciónalo aquí y cópialo a todas las unidades.
              </p>
              <select
                v-model="modeloGlobal"
                class="unidades-input unidades-input--light unidades-input--compact"
                @change="modeloGlobalAplicado = false"
              >
                <option value="">Selecciona un modelo…</option>
                <option
                  v-for="modelo in modelosProductoSeleccionado"
                  :key="'atajo-modelo-' + modelo.id_modelo_producto"
                  :value="String(modelo.id_modelo_producto)"
                >
                  {{ modelo.nombre }} · {{ modelo.modelo }}
                </option>
              </select>
              <div class="unidades-form-shortcut-actions unidades-form-shortcut-actions--start">
                <button
                  type="button"
                  class="ios-ghost-button"
                  :disabled="!modeloGlobal"
                  @click="aplicarModeloGlobalATodas"
                >
                  Aplicar este modelo a todas las unidades
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Bloque de unidades -->
        <section
          v-if="camposSeriales.length && form.id_producto"
          class="unidades-form-units"
        >
          <div
            v-for="(item, index) in camposSeriales"
            :key="index"
            class="unidad-card"
          >
            <div class="unidad-card__header">
              <div class="unidad-card__title">
                <span class="unidad-card__badge">
                  Unidad #{{ index + 1 }}
                </span>
                <span class="unidad-card__product">
                  {{ nombreProductoSeleccionado }}
                </span>
              </div>
            </div>

            <!-- Serie principal -->
            <div class="unidad-card__field">
              <label class="unidad-card__label">
                Serie del producto principal (opcional)
              </label>
              <input
                v-model="item.serie_principal"
                class="unidades-input unidades-input--light"
                :placeholder="nombreProductoSeleccionado + ' - ' + (index + 1)"
              />
            </div>

            <div class="unidad-card__grid unidad-card__grid--two-cols">
              <div class="unidad-card__field">
                <label class="unidad-card__label">Categoría de la unidad</label>
                <select
                  v-model="item.categoria_unidad"
                  class="unidades-input unidades-input--light"
                >
                  <option value="nuevo">Nuevo</option>
                  <option value="usado">Usado</option>
                </select>
              </div>

              <div
                v-if="modelosProductoSeleccionado.length"
                class="unidad-card__field"
              >
                <label class="unidad-card__label">Modelo del producto</label>
                <select
                  v-model="item.id_modelo_producto"
                  class="unidades-input unidades-input--light"
                >
                  <option value="">Selecciona un modelo…</option>
                  <option
                    v-for="modelo in modelosProductoSeleccionado"
                    :key="modelo.id_modelo_producto"
                    :value="String(modelo.id_modelo_producto)"
                  >
                    {{ modelo.nombre }} · {{ modelo.modelo }}
                  </option>
                </select>
              </div>
            </div>

            <section
              v-if="evidenciaModo === 'individual'"
              class="unidad-card__evidencia"
            >
              <div class="unidad-card__evidencia-grid">
                <div class="unidad-card__field unidad-card__field--grow">
                  <label class="unidad-card__label">
                    Descripción del estado inicial
                  </label>
                  <textarea
                    v-model="item.descripcion_estado"
                    class="unidades-input unidades-input--light unidades-input--textarea"
                    rows="3"
                    :placeholder="'Ej. Estado de la unidad #' + (index + 1)"
                  ></textarea>
                </div>

                <div class="unidad-card__field unidad-card__field--media">
                  <label class="unidad-card__label">
                    Foto de la unidad
                  </label>

                  <div class="unidades-photo-box unidades-photo-box--compact">
                    <div v-if="item.imagen_estado_preview" class="unidades-photo-preview unidades-photo-preview--compact">
                      <img :src="item.imagen_estado_preview" :alt="'Estado inicial de la unidad ' + (index + 1)" />
                    </div>
                    <div v-else class="unidades-photo-empty unidades-photo-empty--compact">
                      Sin foto individual
                    </div>

                    <input
                      :ref="'inputImagenEstadoUnidad_' + index"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      class="unidades-photo-input"
                      @change="onImagenEstadoUnidadSeleccionada($event, index)"
                    />

                    <div class="unidades-photo-actions">
                      <button
                        type="button"
                        class="ios-ghost-button"
                        @click="abrirSelectorImagenUnidad(index)"
                      >
                        {{ item.imagen_estado_preview ? 'Cambiar foto' : 'Cargar foto' }}
                      </button>

                      <button
                        v-if="item.imagen_estado_preview"
                        type="button"
                        class="ios-ghost-button"
                        @click="quitarImagenEstadoUnidad(index)"
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div class="unidad-card__field unidad-card__field--grow">
              <label class="unidad-card__label">Ubicación individual de esta unidad</label>
              <SelectorUbicacionAlmacen
                :model-value="{ id_almacen: item.id_almacen, id_ubicacion_almacen: item.id_ubicacion_almacen }"
                :almacenes="almacenesCatalogo"
                :mostrar-labels="false"
                @update:modelValue="(value) => onUbicacionUnidadChange(item, value)"
                @created="(evento) => onUbicacionCreadaUnidad(item, evento)"
              />
            </div>

            <!-- Complementos -->
            <div
              v-for="complemento in complementos"
              :key="complemento.id_producto"
              class="unidad-card__field"
            >
              <label class="unidad-card__label">
                Serie del complemento (opcional)
                <span class="unidad-card__label-pill">
                  {{ complemento.nombre }}
                </span>
              </label>
              <input
                v-model="item.complementos[complemento.id_producto]"
                class="unidades-input unidades-input--light"
                :placeholder="complemento.nombre + ' - ' + (index + 1)"
              />
            </div>
          </div>
        </section>

        <!-- Footer acciones -->
        <footer class="unidades-form-footer">
          <button
            type="button"
            class="ios-ghost-button"
            @click="$emit('cerrar')"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="ios-primary-button"
          >
            Guardar unidades
          </button>
        </footer>
      </form>
    </div>

    <!-- SUBMODAL: selección de producto -->
    <transition name="fade-scale">
      <div
        v-if="mostrarModalProducto"
        class="submodal-overlay"
        @click.self="cerrarModalProducto"
      >
        <div class="submodal-card" @click.stop>
          <header class="submodal-header">
            <p class="submodal-title">
              Seleccionar producto principal
            </p>
            <p class="submodal-subtitle">
              Elige el producto al que se asociarán las unidades.
            </p>
          </header>

          <div class="submodal-body">
            <SelectorProducto
              :productos="productosPrincipales"
              v-model="form.id_producto"
              @seleccionado="alSeleccionarProductoDesdeModal"
            />
          </div>

          <footer class="submodal-footer">
            <button
              type="button"
              class="submodal-btn submodal-btn--ghost"
              @click="cerrarModalProducto"
            >
              Cerrar
            </button>
          </footer>
        </div>
      </div>
    </transition>

    <!-- SUBMODAL: cantidad con teclado numérico -->
    <transition name="fade-scale">
      <div
        v-if="mostrarModalCantidad"
        class="submodal-overlay"
        @click.self="cerrarModalCantidad"
      >
        <div class="submodal-card submodal-card--small" @click.stop>
          <header class="submodal-header">
            <p class="submodal-title">
              Cantidad de unidades
            </p>
            <p class="submodal-subtitle">
              Usa los botones o el teclado para definir cuántas unidades registrarás.
            </p>
          </header>

          <div class="cantidad-modal-body">
            <div class="cantidad-display">
              <button
                type="button"
                class="cantidad-steper"
                @click="ajustarCantidadKeypad(-1)"
              >
                −
              </button>

              <div class="cantidad-display-value">
                {{ cantidadDesdeKeypad }}
              </div>

              <button
                type="button"
                class="cantidad-steper"
                @click="ajustarCantidadKeypad(1)"
              >
                +
              </button>
            </div>

            <div class="cantidad-keypad-grid">
              <button
                v-for="n in [1,2,3,4,5,6,7,8,9]"
                :key="n"
                type="button"
                class="cantidad-keypad-btn"
                @click="appendDigit(n)"
              >
                {{ n }}
              </button>

              <button
                type="button"
                class="cantidad-keypad-btn cantidad-keypad-btn--secondary"
                @click="borrarUltimo"
              >
                ⌫
              </button>

              <button
                type="button"
                class="cantidad-keypad-btn"
                @click="appendDigit(0)"
              >
                0
              </button>

              <button
                type="button"
                class="cantidad-keypad-btn cantidad-keypad-btn--secondary"
                @click="limpiarCantidadKeypad"
              >
                C
              </button>
            </div>
          </div>

          <footer class="submodal-footer">
            <button
              type="button"
              class="submodal-btn submodal-btn--ghost"
              @click="cerrarModalCantidad"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="submodal-btn submodal-btn--primary"
              @click="confirmarCantidadDesdeModal"
            >
              Aceptar
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import SelectorProducto from '@/components/ui/SelectorProducto.vue';
import SelectorUbicacionAlmacen from '@/components/almacen/almacenes/SelectorUbicacionAlmacen.vue';

export default {
  name: 'FormularioUnidades',
  components: { SelectorProducto, SelectorUbicacionAlmacen },
  props: {
    // Se mantiene el prop por compatibilidad, pero la carga principal
    // de productos ahora se hace desde este componente.
    productos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        id_producto: '',
      },
      cantidad: 1,
      camposSeriales: [],
      complementos: [],
      productosCargados: [],
      almacenesCatalogo: [],
      ubicacionGlobal: {
        id_almacen: '',
        id_ubicacion_almacen: '',
      },
      categoriaGlobal: 'nuevo',
      categoriaGlobalAplicada: false,
      modeloGlobal: '',
      modeloGlobalAplicado: false,
      evidenciaModo: 'global',
      ubicacionGlobalAplicada: false,
      descripcionEstado: '',
      imagenEstadoBase64: '',
      imagenEstadoNombre: '',
      imagenEstadoPreview: '',

      // Wizard / submodales
      mostrarModalProducto: false,
      mostrarModalCantidad: false,

      // keypad
      keypadValue: '',
      keypadStarted: false,
    };
  },
  computed: {
    // Fuente base de productos: primero el prop si viene con datos,
    // si no, los que cargamos desde este componente.
    productosBase() {
      const desdeProp = Array.isArray(this.productos) ? this.productos : [];
      if (desdeProp.length > 0) {
        return desdeProp;
      }
      return Array.isArray(this.productosCargados)
        ? this.productosCargados
        : [];
    },
    productosPrincipales() {
      return (this.productosBase || []).filter(
        (p) => p.tipo_producto === 'principal'
      );
    },
    productoSeleccionado() {
      return (this.productosBase || []).find(
        (p) => String(p.id_producto) === String(this.form.id_producto)
      ) || null;
    },
    modelosProductoSeleccionado() {
      return Array.isArray(this.productoSeleccionado?.modelos)
        ? this.productoSeleccionado.modelos
        : [];
    },
    nombreProductoSeleccionado() {
      return this.productoSeleccionado ? this.productoSeleccionado.nombre : 'Producto';
    },
    cantidadDesdeKeypad() {
      // Si aún no ha empezado a escribir, mostramos la cantidad actual (o 1)
      let base;
      if (!this.keypadStarted || !this.keypadValue) {
        base = this.cantidad || 1;
      } else {
        base = parseInt(this.keypadValue, 10);
      }

      if (Number.isNaN(base) || base < 1) base = 1;
      if (base > 999) base = 999;
      return base;
    },
  },
  mounted() {
    Promise.all([this.cargarProductos(), this.cargarAlmacenes()]).finally(() => {
      this.mostrarModalProducto = true;
    });
  },
  methods: {
    async archivoABase64(file) {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    async onImagenEstadoSeleccionada(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      try {
        this.imagenEstadoBase64 = await this.archivoABase64(file);
        this.imagenEstadoNombre = file.name || 'estado-unidad';
        this.imagenEstadoPreview = this.imagenEstadoBase64;
      } catch (error) {
        console.error('Error al leer la imagen del estado inicial:', error);
        this.$toast?.error('No se pudo cargar la imagen del estado inicial.');
      }
    },

    quitarImagenEstado() {
      this.imagenEstadoBase64 = '';
      this.imagenEstadoNombre = '';
      this.imagenEstadoPreview = '';
      if (this.$refs.inputImagenEstado) {
        this.$refs.inputImagenEstado.value = '';
      }
    },

    abrirSelectorImagenUnidad(index) {
      const ref = this.$refs['inputImagenEstadoUnidad_' + index];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input) {
        input.click();
      }
    },

    async onImagenEstadoUnidadSeleccionada(event, index) {
      const file = event?.target?.files?.[0];
      if (!file || !this.camposSeriales[index]) return;

      try {
        const base64 = await this.archivoABase64(file);
        const item = this.camposSeriales[index];
        item.imagen_estado_base64 = base64;
        item.imagen_estado_nombre = file.name || ('estado-unidad-' + (index + 1));
        item.imagen_estado_preview = base64;
      } catch (error) {
        console.error('Error al leer la imagen individual:', error);
        this.$toast?.error('No se pudo cargar la imagen de la unidad.');
      }
    },

    quitarImagenEstadoUnidad(index) {
      const item = this.camposSeriales[index];
      if (!item) return;
      item.imagen_estado_base64 = '';
      item.imagen_estado_nombre = '';
      item.imagen_estado_preview = '';
      const ref = this.$refs['inputImagenEstadoUnidad_' + index];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input) {
        input.value = '';
      }
    },

    async cargarProductos() {
      try {
        const res = await axios.get('/api/almacen/productos/listar');
        this.productosCargados = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        console.error('Error al cargar productos:', error);
        this.productosCargados = [];
        this.$toast?.error('No se pudieron cargar los productos.');
      }
    },

    async cargarAlmacenes() {
      try {
        const { data } = await axios.get('/api/almacen/almacenes/catalogo');
        this.almacenesCatalogo = data?.data || [];
      } catch (error) {
        console.error('Error al cargar catálogo de almacenes:', error);
        this.almacenesCatalogo = [];
      }
    },

    onUbicacionGlobalChange(value) {
      this.ubicacionGlobal = {
        id_almacen: value?.id_almacen || '',
        id_ubicacion_almacen: value?.id_ubicacion_almacen || '',
      };
      this.ubicacionGlobalAplicada = false;
    },

    onUbicacionUnidadChange(item, value) {
      if (!item) return;
      item.id_almacen = value?.id_almacen || '';
      item.id_ubicacion_almacen = value?.id_ubicacion_almacen || '';
    },

    onUbicacionCreadaGlobal(evento) {
      this.registrarUbicacionCreada(evento, 'global');
    },

    onUbicacionCreadaUnidad(item, evento) {
      this.registrarUbicacionCreada(evento, 'unidad', item);
    },

    registrarUbicacionCreada(evento, contexto = 'unidad', item = null) {
      const nuevaUbicacion = this.normalizarUbicacionCreada(evento);
      if (!nuevaUbicacion) return;

      this.insertarUbicacionEnCatalogo(nuevaUbicacion);

      if (contexto === 'global') {
        this.ubicacionGlobal = {
          id_almacen: nuevaUbicacion.id_almacen,
          id_ubicacion_almacen: nuevaUbicacion.id_ubicacion_almacen,
        };
        this.ubicacionGlobalAplicada = false;
      }

      if (item) {
        item.id_almacen = nuevaUbicacion.id_almacen;
        item.id_ubicacion_almacen = nuevaUbicacion.id_ubicacion_almacen;
      }

    },

    normalizarUbicacionCreada(evento) {
      const payload = evento?.payload || {};
      const idAlmacen = String(payload.id_almacen || evento?.id_almacen || '');
      const idUbicacion = String(evento?.id_ubicacion_almacen || payload.id_ubicacion_almacen || '');

      if (!idAlmacen || !idUbicacion) {
        return null;
      }

      const nivel1 = payload.nivel_1 || '';
      const nivel2 = payload.nivel_2 || '';
      const nivel3 = payload.nivel_3 || '';
      const referencia = payload.referencia || '';

      return {
        id_ubicacion_almacen: idUbicacion,
        id_almacen: idAlmacen,
        nivel_1: nivel1,
        nivel_2: nivel2,
        nivel_3: nivel3,
        referencia,
        ubicacion_label: this.construirUbicacionLabel(nivel1, nivel2, nivel3),
      };
    },

    construirUbicacionLabel(nivel1, nivel2, nivel3) {
      return [nivel1, nivel2, nivel3].filter(Boolean).join(' / ');
    },

    insertarUbicacionEnCatalogo(ubicacion) {
      if (!ubicacion?.id_almacen || !ubicacion?.id_ubicacion_almacen) return;

      const catalogo = Array.isArray(this.almacenesCatalogo)
        ? JSON.parse(JSON.stringify(this.almacenesCatalogo))
        : [];

      const idxAlmacen = catalogo.findIndex(
        (almacen) => String(almacen.id_almacen) === String(ubicacion.id_almacen),
      );
      if (idxAlmacen === -1) return;

      const almacen = catalogo[idxAlmacen];
      if (!Array.isArray(almacen.ubicaciones)) {
        almacen.ubicaciones = [];
      }

      const existente = almacen.ubicaciones.find(
        (item) => String(item.id_ubicacion_almacen) === String(ubicacion.id_ubicacion_almacen),
      );

      if (existente) {
        Object.assign(existente, ubicacion);
      } else {
        almacen.ubicaciones.unshift(ubicacion);
      }

      this.almacenesCatalogo = catalogo;
    },

    aplicarUbicacionGlobalATodas() {
      if (!this.ubicacionGlobal.id_almacen || !this.ubicacionGlobal.id_ubicacion_almacen) {
        this.$toast?.info('Selecciona una ubicación completa antes de aplicarla a la carga.');
        return;
      }

      this.camposSeriales = (this.camposSeriales || []).map((item) => ({
        ...item,
        id_almacen: this.ubicacionGlobal.id_almacen,
        id_ubicacion_almacen: this.ubicacionGlobal.id_ubicacion_almacen,
      }));
      this.ubicacionGlobalAplicada = true;
      this.$toast?.success('La ubicación se aplicó a todas las unidades de esta carga.');
    },

    aplicarCategoriaGlobalATodas() {
      const categoria = this.categoriaGlobal || 'nuevo';
      this.camposSeriales = (this.camposSeriales || []).map((item) => ({
        ...item,
        categoria_unidad: categoria,
      }));
      this.categoriaGlobalAplicada = true;
      this.$toast?.success('La categoría se aplicó a todas las unidades de esta carga.');
    },

    aplicarModeloGlobalATodas() {
      if (!this.modeloGlobal) {
        this.$toast?.info('Selecciona un modelo antes de aplicarlo a toda la carga.');
        return;
      }

      this.camposSeriales = (this.camposSeriales || []).map((item) => ({
        ...item,
        id_modelo_producto: String(this.modeloGlobal),
      }));
      this.modeloGlobalAplicado = true;
      this.$toast?.success('El modelo se aplicó a todas las unidades de esta carga.');
    },

    // --- Cantidad inline ---
    onCantidadInput() {
      // Normalizamos a mínimo 1 y a número entero
      if (!this.cantidad || this.cantidad < 1) {
        this.cantidad = 1;
      } else {
        this.cantidad = Math.floor(this.cantidad);
      }
      this.generarCampos();
    },

    incrementCantidad() {
      if (!this.cantidad || this.cantidad < 1) {
        this.cantidad = 1;
      } else {
        this.cantidad = Math.floor(this.cantidad) + 1;
      }
      this.generarCampos();
    },

    decrementCantidad() {
      if (!this.cantidad || this.cantidad <= 1) {
        this.cantidad = 1;
      } else {
        this.cantidad = Math.floor(this.cantidad) - 1;
      }
      this.generarCampos();
    },

    // --- Selección de producto (inline y modal) ---
    alSeleccionarProducto(prod) {
      this.form.id_producto = prod?.id_producto || '';
      this.cantidad = 1;
      this.categoriaGlobal = 'nuevo';
      this.categoriaGlobalAplicada = false;
      this.modeloGlobal = '';
      this.modeloGlobalAplicado = false;
      this.generarCampos();

      if (this.form.id_producto) {
        this.cargarComplementos(this.form.id_producto);
      } else {
        this.complementos = [];
        this.camposSeriales = [];
      }
    },

    alSeleccionarProductoDesdeModal(prod) {
      this.alSeleccionarProducto(prod);
      this.mostrarModalProducto = false;

      if (this.form.id_producto) {
        // Lanzamos directo el modal de cantidad (wizard)
        this.abrirModalCantidadWizard();
      }
    },

    // --- Campos por unidad ---
    generarCampos() {
      const cantidad = Number(this.cantidad) || 0;
      if (cantidad <= 0) {
        this.camposSeriales = [];
        return;
      }

      this.camposSeriales = Array.from({ length: cantidad }, (_, index) => {
        const anterior = this.camposSeriales[index] || {};
        const usarGlobal = !anterior.serie_principal && !anterior.id_ubicacion_almacen && this.ubicacionGlobalAplicada;
        const categoriaPorDefecto = this.categoriaGlobalAplicada
          ? (this.categoriaGlobal || 'nuevo')
          : 'nuevo';
        const modeloPorDefecto = this.modeloGlobalAplicado
          ? String(this.modeloGlobal || '')
          : '';
        const complementos = {};

        (this.complementos || []).forEach((c) => {
          complementos[c.id_producto] =
            anterior?.complementos?.[c.id_producto] || '';
        });

        return {
          serie_principal: anterior.serie_principal || '',
          categoria_unidad: anterior.categoria_unidad || categoriaPorDefecto,
          id_modelo_producto: anterior.id_modelo_producto || modeloPorDefecto,
          complementos,
          descripcion_estado: anterior.descripcion_estado || '',
          imagen_estado_base64: anterior.imagen_estado_base64 || '',
          imagen_estado_nombre: anterior.imagen_estado_nombre || '',
          imagen_estado_preview: anterior.imagen_estado_preview || '',
          id_almacen: anterior.id_almacen || (usarGlobal ? this.ubicacionGlobal.id_almacen || '' : ''),
          id_ubicacion_almacen: anterior.id_ubicacion_almacen || (usarGlobal ? this.ubicacionGlobal.id_ubicacion_almacen || '' : ''),
        };
      });
    },

    async cargarComplementos(id_producto_principal) {
      try {
        const res = await axios.get(
          '/api/almacen/productos/complementos/' + id_producto_principal
        );
        this.complementos = Array.isArray(res.data) ? res.data : [];
        this.generarCampos(); // regenerar campos con nuevos complementos
      } catch (error) {
        console.error('Error al obtener complementos:', error);
        this.complementos = [];
        this.camposSeriales = [];
        this.$toast?.error(
          'No se pudieron cargar los complementos del producto seleccionado.'
        );
      }
    },

    // --- Submodal producto ---
    cerrarModalProducto() {
      this.mostrarModalProducto = false;
    },

    // --- Submodal cantidad + keypad ---
    abrirModalCantidadWizard() {
      // No seteamos un número por defecto en el keypad,
      // sólo usamos la cantidad actual como referencia visual.
      this.keypadValue = '';
      this.keypadStarted = false;
      this.mostrarModalCantidad = true;
    },

    abrirCantidadModalDesdeInput() {
      // UX móvil: al enfocar el input, abrimos el modal de teclado
      this.keypadValue = '';
      this.keypadStarted = false;
      this.mostrarModalCantidad = true;
    },

    cerrarModalCantidad() {
      // Si cierra sin aceptar, dejamos la cantidad tal cual estaba (por defecto 1)
      this.mostrarModalCantidad = false;
    },

    appendDigit(d) {
      // Primer dígito: reemplaza el valor previo
      if (!this.keypadStarted || !this.keypadValue) {
        this.keypadStarted = true;
        this.keypadValue = String(d);
        return;
      }

      if (this.keypadValue.length >= 3) return;

      const next = this.keypadValue + String(d);
      this.keypadValue = next.replace(/^0+/, '') || '0';
    },

    borrarUltimo() {
      if (!this.keypadStarted || !this.keypadValue) return;

      const next = this.keypadValue.slice(0, -1);
      if (!next) {
        this.keypadValue = '';
        this.keypadStarted = false;
      } else {
        this.keypadValue = next;
      }
    },

    limpiarCantidadKeypad() {
      this.keypadValue = '';
      this.keypadStarted = false;
    },

    ajustarCantidadKeypad(delta) {
      let base;
      if (!this.keypadStarted || !this.keypadValue) {
        base = this.cantidad || 1;
      } else {
        base = parseInt(this.keypadValue, 10);
      }

      if (Number.isNaN(base) || base < 1) base = 1;
      let n = base + delta;
      if (n < 1) n = 1;
      if (n > 999) n = 999;

      this.keypadStarted = true;
      this.keypadValue = String(n);
    },

    confirmarCantidadDesdeModal() {
      const n = this.cantidadDesdeKeypad;
      this.cantidad = n;
      this.generarCampos();
      this.mostrarModalCantidad = false;
    },

    // --- Guardar ---
    async guardar() {
      if (!this.form.id_producto) {
        this.$toast?.error(
          'Selecciona un producto principal antes de guardar.'
        );
        return;
      }

      if (
        !Array.isArray(this.camposSeriales) ||
        this.camposSeriales.length === 0
      ) {
        this.$toast?.error(
          'Debes registrar al menos una unidad antes de guardar.'
        );
        return;
      }

      const requiereModelo = this.modelosProductoSeleccionado.length > 0;
      const unidadSinModelo = requiereModelo
        ? this.camposSeriales.find((item) => !item.id_modelo_producto)
        : null;

      if (unidadSinModelo) {
        this.$toast?.error('Selecciona el modelo de cada unidad antes de guardar.');
        return;
      }

      try {
        await axios.post('/api/almacen/unidades/registrar-relacionadas', {
          id_producto: this.form.id_producto,
          unidades: this.camposSeriales.map((item) => ({
            serie_principal: item.serie_principal,
            categoria_unidad: item.categoria_unidad || 'nuevo',
            id_modelo_producto: item.id_modelo_producto || '',
            complementos: item.complementos,
            descripcion_estado: this.evidenciaModo === 'individual' ? (item.descripcion_estado || '') : '',
            imagen_estado_base64: this.evidenciaModo === 'individual' ? (item.imagen_estado_base64 || '') : '',
            imagen_estado_nombre: this.evidenciaModo === 'individual' ? (item.imagen_estado_nombre || '') : '',
            id_almacen: item.id_almacen || '',
            id_ubicacion_almacen: item.id_ubicacion_almacen || '',
          })),
          descripcion_estado: this.evidenciaModo === 'global' ? this.descripcionEstado : '',
          imagen_estado_base64: this.evidenciaModo === 'global' ? this.imagenEstadoBase64 : '',
          imagen_estado_nombre: this.evidenciaModo === 'global' ? this.imagenEstadoNombre : '',
          id_almacen: '',
          id_ubicacion_almacen: '',
        });

        this.$toast?.success('Unidades registradas correctamente.');
        this.$emit('guardado');
      } catch (error) {
        console.error('Error al guardar unidades:', error);
        this.$toast?.error('Ocurrió un error al guardar las unidades.');
      }
    },
  },
};
</script>

<style scoped>
.unidades-form-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.35),
    rgba(15, 23, 42, 0.95)
  );
  backdrop-filter: blur(26px);
}

.unidades-form-card {
  position: relative;
  width: 100%;
  max-width: 960px;
  max-height: 85vh;
  border-radius: 1.8rem;
  padding: 1.4rem 1.4rem 1.1rem;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.36),
    rgba(15, 23, 42, 0.98)
  );
  border: 1px solid rgba(148, 163, 184, 0.75);
  box-shadow:
    0 24px 55px rgba(15, 23, 42, 0.95),
    0 0 0 1px rgba(15, 23, 42, 0.9);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.unidades-form-close {
  position: absolute;
  top: 0.4rem;
  right: 0.7rem;
  border: none;
  background: transparent;
  color: #e5e7eb;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.2rem;
}

/* Header */
.unidades-form-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.unidades-form-header-main {
  display: flex;
  flex-direction: column;
  gap: 0.16rem;
}

.unidades-form-title {
  font-size: 0.9rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #cbd5f5;
}

.unidades-form-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Body */
.unidades-form-body {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding-top: 0.6rem;
  padding-right: 0.35rem;
  border-top: 1px solid rgba(148, 163, 184, 0.55);
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.unidades-form-body::-webkit-scrollbar {
  width: 8px;
}

.unidades-form-body::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 999px;
}

.unidades-form-body::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.65);
  border-radius: 999px;
}

.unidades-form-mode {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.5);
  padding: 0.85rem;
  border-radius: 1rem;
}

.unidades-form-mode__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.unidades-mode-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.unidades-mode-toggle__btn {
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.72);
  color: #cbd5f5;
  border-radius: 999px;
  padding: 0.5rem 0.8rem;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.18s ease;
}

.unidades-mode-toggle__btn--active {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(96, 165, 250, 0.7);
  color: #eff6ff;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.25) inset;
}

.unidades-form-label--strong {
  font-weight: 600;
}

.unidades-form-state {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 0.8rem;
  align-items: start;
}

.unidades-form-state--single {
  grid-template-columns: 1fr;
}

.unidades-photo-box {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.unidades-photo-preview,
.unidades-photo-empty {
  width: 100%;
  height: 152px;
  border-radius: 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.88);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.8rem;
}

.unidades-photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.unidades-photo-box--compact {
  gap: 0.45rem;
}

.unidades-photo-preview--compact,
.unidades-photo-empty--compact {
  height: 132px;
}

.unidades-photo-input {
  display: none;
}

.unidades-photo-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.unidades-form-shortcut-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.55rem;
}

.unidades-form-shortcut-actions--start {
  justify-content: flex-start;
}

.unidades-bulk-shortcuts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.8rem;
}

.unidades-bulk-shortcuts__card {
  border: 1px solid rgba(148, 163, 184, 0.32);
  background: rgba(15, 23, 42, 0.42);
  border-radius: 1rem;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.42rem;
}

/* Row principal */
.unidades-form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
}

.unidades-form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.unidades-form-field--grow {
  flex: 1 1 0;
  min-width: 0;
}

.unidades-form-field--compact {
  width: 140px;
}

.unidades-form-label {
  font-size: 0.78rem;
  color: #cbd5f5;
}

/* Hint */
.unidades-form-hint {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Inputs */
.unidades-input {
  width: 100%;
  border-radius: 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  padding: 0.45rem 0.7rem;
  font-size: 0.8rem;
  outline: none;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.unidades-input--compact {
  min-height: 40px;
}

.unidades-input--light {
  background: rgba(15, 23, 42, 0.92);
}

.unidades-input::placeholder {
  color: #6b7280;
}

.unidades-input:focus {
  border-color: rgba(129, 140, 248, 0.95);
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.7);
}

/* Lista de unidades */
.unidades-form-units {
  margin-top: 0.2rem;
  border-radius: 1.1rem;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.6rem;
  flex: 1 1 auto;
  min-height: 600px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Scroll fino */
.unidades-form-units::-webkit-scrollbar {
  width: 6px;
}
.unidades-form-units::-webkit-scrollbar-track {
  background: transparent;
}
.unidades-form-units::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.55);
  border-radius: 999px;
}

/* Card de cada unidad */
.unidad-card {
  border-radius: 1rem;
  padding: 0.65rem 0.7rem 0.6rem;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.32),
    rgba(15, 23, 42, 0.95)
  );
  border: 1px solid rgba(148, 163, 184, 0.55);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.75);
  margin-bottom: 0.55rem;
}

.unidad-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.unidad-card__title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.unidad-card__badge {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.96);
}

.unidad-card__product {
  font-size: 0.8rem;
  color: #e5e7eb;
}

.unidad-card__field {
  margin-top: 0.45rem;
}

.unidad-card__grid {
  display: grid;
  gap: 0.7rem;
  margin-top: 0.45rem;
}

.unidad-card__grid--two-cols {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.unidad-card__field--grow {
  min-width: 0;
}

.unidad-card__field--media {
  width: 230px;
}

.unidad-card__evidencia {
  margin-top: 0.7rem;
  padding-top: 0.7rem;
  border-top: 1px dashed rgba(148, 163, 184, 0.35);
}

.unidad-card__evidencia-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 230px;
  gap: 0.8rem;
  align-items: start;
}

.unidad-card__label {
  font-size: 0.75rem;
  color: #cbd5f5;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.unidad-card__label-pill {
  font-size: 0.7rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

/* Footer */
.unidades-form-footer {
  margin-top: 0.7rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.45rem;
}

/* Botones iOS glass */
.ios-primary-button,
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-primary-button {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
  color: #e5e7eb;
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.7);
}

.ios-primary-button:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 18px 38px rgba(37, 99, 235, 0.9);
}

.ios-ghost-button {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
}

/* Counter */
.unidades-counter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.unidades-counter__btn {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.75);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.06s ease;
}

.unidades-counter__btn:hover {
  background: rgba(15, 23, 42, 1);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.9);
}

.unidades-counter__btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 1);
}

.unidades-counter__input {
  max-width: 80px;
  text-align: center;
  padding-inline: 0.4rem;
}

/* Quitar flechas del input number (WebKit) */
.unidades-counter__input::-webkit-inner-spin_button,
.unidades-counter__input::-webkit-outer-spin_button {
  -webkit-appearance: none;
  margin: 0;
}

/* Quitar flechas en Firefox */
.unidades-counter__input[type='number'] {
  -moz-appearance: textfield;
}

/* SUBMODALES */
.submodal-overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: radial-gradient(
    circle at 0 0,
    rgba(15, 23, 42, 0.35),
    rgba(15, 23, 42, 0.92)
  );
  backdrop-filter: blur(16px);
}

.submodal-card {
  width: 100%;
  max-width: 520px;
  border-radius: 1.4rem;
  padding: 1rem 1rem 0.9rem;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.36),
    rgba(15, 23, 42, 0.98)
  );
  border: 1px solid rgba(148, 163, 184, 0.8);
  box-shadow:
    0 20px 52px rgba(15, 23, 42, 0.96),
    0 0 0 1px rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.submodal-card--small {
  max-width: 420px;
}

.submodal-header {
  margin-bottom: 0.6rem;
}

.submodal-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #f9fafb;
}

.submodal-subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 0.2rem;
}

.submodal-body {
  padding-top: 0.25rem;
}

.submodal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  margin-top: 0.8rem;
}

.submodal-btn {
  border-radius: 999px;
  font-size: 0.8rem;
  padding: 0.35rem 0.9rem;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.08s ease;
}

.submodal-btn--ghost {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.submodal-btn--ghost:hover {
  background: rgba(15, 23, 42, 1);
  border-color: rgba(148, 163, 184, 0.95);
}

.submodal-btn--primary {
  background: #e5e7eb;
  color: #020617;
}

.submodal-btn--primary:hover {
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.9);
}

/* Cantidad modal */
.cantidad-modal-body {
  margin-top: 0.35rem;
}

.cantidad-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 0.7rem;
}

.cantidad-display-value {
  min-width: 72px;
  text-align: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.96);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.cantidad-steper {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.85);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.06s ease;
}

.cantidad-steper:hover {
  background: rgba(15, 23, 42, 1);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.9);
}

.cantidad-keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.45rem;
}

.cantidad-keypad-btn {
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  font-size: 0.95rem;
  padding: 0.45rem 0;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.06s ease;
}

.cantidad-keypad-btn--secondary {
  font-size: 0.8rem;
}

.cantidad-keypad-btn:hover {
  background: rgba(15, 23, 42, 1);
  border-color: rgba(148, 163, 184, 0.95);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.9);
}

/* Animación submodales */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.97);
}

/* Responsive */
@media (max-width: 768px) {
  .unidades-form-card {
    padding-inline: 1.1rem;
  }
  .unidades-form-row {
    flex-direction: column;
  }
  .unidades-form-field--compact {
    width: 100%;
  }
}
@media (max-width: 880px) {
  .unidades-form-state,
  .unidad-card__evidencia-grid {
    grid-template-columns: 1fr;
  }

  .unidad-card__field--media {
    width: 100%;
  }
}


@media (max-width: 760px) {
  .unidad-card__grid--two-cols,
  .unidad-card__evidencia-grid,
  .unidades-form-state {
    grid-template-columns: 1fr;
  }

  .unidad-card__field--media {
    width: 100%;
  }
}
</style>

